import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import { Extension } from '@tiptap/core';
import Suggestion from '@tiptap/suggestion';
import {
  detectCode,
  docToPlainText,
  SAFE_URL_RE,
  type PublicUser,
  type RichTextDoc,
  type UserGroup,
} from '@avslack/shared';
import { searchEmoji } from '../data/emoji';
import { createEmojiInputRules } from './emoji-input-rules';
import { useCustomEmoji } from '../data/custom-emoji';
import { useFrequentEmoji } from '../data/emoji-usage';
import { Avatar } from './Avatar';
import { Icon } from './Icon';

export interface ComposerSubmit {
  text: string;
  body: RichTextDoc;
}

interface RichComposerProps {
  placeholder: string;
  users: PublicUser[];
  /** Felhasznaloi csoportok (@fejlesztok) az emlites-listahoz. */
  groups?: UserGroup[];
  meId: string;
  /** Csatorna-szintu emlitesek (@channel, @here) csak a fo szerkesztoben. */
  allowChannelMentions?: boolean;
  autoFocus?: boolean;
  /** Fajl-melleklet mellett ures szoveggel is lehet kuldeni. */
  allowEmpty?: boolean;
  compact?: boolean;
  initial?: RichTextDoc | undefined;
  submitLabel?: string;
  onSubmit: (value: ComposerSubmit) => void;
  onTyping?: () => void;
  onCancel?: () => void;
  /** Minden valtozasnal - a hivo dolga a kesleltetes (piszkozat-mentes). */
  onChange?: (value: ComposerSubmit) => void;
  /** A szerkeszto vezerlese kivulrol (pl. utemezes utani uritas). */
  onReady?: (api: ComposerApi) => void;
  /**
   * A formazo sor JOBB VEGERE kerul (pl. a "Fontos" jeloles).
   *
   * Egy sorban a formazo gombokkal, hogy ne kulon sort egyen meg a mezo
   * tetejen - a szerkeszto igy egy sorral alacsonyabb.
   */
  toolbarEnd?: React.ReactNode;
}

export interface ComposerApi {
  clear: () => void;
  getValue: () => ComposerSubmit | null;
  /** Szoveg beszurasa a kurzorhoz - az emoji-valaszto hasznalja. */
  insert: (text: string) => void;
  /** Kurzor a szerkesztobe, oda, ahol utoljara allt. */
  focus: () => void;
}

interface MentionItem {
  id: string;
  label: string;
  kind: 'user' | 'channel' | 'here' | 'group' | 'emoji';
  hint: string;
  /**
   * Emoji-javaslatnal: mi kerul a szovegbe, es mit mutatunk a listaban.
   *
   * A beszurando NEM mindig a felirat: az egyedi emojiknal `:nev:` alak megy a
   * szovegbe (abbol lesz kep a megjelenitesnel), a beepitetteknel maga a jel.
   */
  insert?: string;
  previewChar?: string;
  previewUrl?: string;
}

interface SuggestionState {
  items: MentionItem[];
  command: (item: MentionItem) => void;
  rect: DOMRect | null;
}

/** Ekezet-fuggetlen egyezes, hogy a "@eva" a "Kis Éva"-t is megtalalja. */
const fold = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

export function RichComposer({
  placeholder,
  users,
  groups = [],
  meId,
  allowChannelMentions = true,
  autoFocus = false,
  allowEmpty = false,
  compact = false,
  initial,
  submitLabel = 'Küldés',
  onSubmit,
  onTyping,
  onCancel,
  onChange,
  onReady,
  toolbarEnd,
}: RichComposerProps) {
  const [suggestion, setSuggestion] = useState<SuggestionState | null>(null);
  const [cursor, setCursor] = useState(0);
  const [isEmpty, setIsEmpty] = useState(true);

  // A ProseMirror-bovitmenyek a letrehozasukkor rogzitett fuggvenyeket hasznaljak,
  // ezert a valtozo adatok (userlista, kijelolt sor) refben elnek.
  const usersRef = useRef(users);
  const groupsRef = useRef(groups);
  const changeRef = useRef(onChange);
  const cursorRef = useRef(0);
  const suggestionRef = useRef<SuggestionState | null>(null);
  const submitRef = useRef(onSubmit);
  const placeholderRef = useRef(placeholder);

  usersRef.current = users;
  placeholderRef.current = placeholder;
  groupsRef.current = groups;
  changeRef.current = onChange;
  cursorRef.current = cursor;
  suggestionRef.current = suggestion;
  submitRef.current = onSubmit;

  const { byName: customEmoji } = useCustomEmoji();
  const customEmojiRef = useRef(customEmoji);
  customEmojiRef.current = customEmoji;
  // A javaslatok a SAJAT hasznalat szerint allnak sorba (lasd searchEmoji).
  const { rank: emojiRank } = useFrequentEmoji();
  const emojiRankRef = useRef(emojiRank);
  emojiRankRef.current = emojiRank;

  /**
   * Emoji-javaslatok a `:` utan.
   *
   * ─── Miert csak ket betutol? ────────────────────────────────────────────────
   *
   * Egyetlen betu utan majdnem az egesz keszlet illeszkedik - abbol a listabol
   * nem valasztani lehet, hanem gorgetni. Ket betu mar szukit annyira, hogy a
   * talalat a lista elejen legyen.
   *
   * A kulcsszavak MAGYARUL vannak (lasd `data/emoji.ts`), tehat itt is magyarul
   * kell keresni: `:mosoly`, nem `:smile`. Ez a keszlet lenyege - aki "kutya"-t
   * ir be, annak nem segit, hogy a jel angol neve `dog face`.
   */
  /**
   * A javaslo-lista billentyui - EGY helyen, mindket fajtara.
   *
   * A nyilak lepkednek, a Tab es az Enter beszur, az Escape bezar. Ugyanaz a
   * mozdulatsor az emlitesnel es az emojinal: aki az egyiket megtanulta, a
   * masikat mar tudja.
   */
  const handleSuggestionKey = useCallback((event: KeyboardEvent): boolean => {
    const current = suggestionRef.current;
    if (!current || current.items.length === 0) return false;

    if (event.key === 'ArrowDown') {
      setCursor((c) => (c + 1) % current.items.length);
      return true;
    }
    if (event.key === 'ArrowUp') {
      setCursor((c) => (c - 1 + current.items.length) % current.items.length);
      return true;
    }
    if (event.key === 'Enter' || event.key === 'Tab') {
      const item = current.items[cursorRef.current];
      if (item) current.command(item);
      return true;
    }
    if (event.key === 'Escape') {
      setSuggestion(null);
      return true;
    }
    return false;
  }, []);

  const buildEmojiItems = useCallback((query: string): MentionItem[] => {
    if (query.length < 2) return [];

    const items: MentionItem[] = [];

    // Elobb az EGYEDI emojik: azok a csapat sajatjai, ott a talalat szandekos.
    // Az egyedi emojik kozul is a sajat kedvencek elore.
    const customHits = [...customEmojiRef.current]
      .filter(([name]) => name.includes(query.toLowerCase()))
      .sort(
        ([a], [b]) =>
          (emojiRankRef.current.get(`:${b}:`) ?? 0) - (emojiRankRef.current.get(`:${a}:`) ?? 0),
      );
    for (const [name, emoji] of customHits) {
      items.push({
        id: `custom-${name}`,
        label: `:${name}:`,
        kind: 'emoji',
        hint: 'egyedi',
        insert: `:${name}:`,
        previewUrl: emoji.url,
      });
      if (items.length >= 4) break;
    }

    for (const group of searchEmoji(query, emojiRankRef.current)) {
      for (const entry of group.items) {
        items.push({
          id: entry.char,
          label: entry.keywords[0] ?? entry.char,
          kind: 'emoji',
          hint: '',
          insert: entry.char,
          previewChar: entry.char,
        });
        if (items.length >= 8) break;
      }
      if (items.length >= 8) break;
    }

    return items;
  }, []);

  const buildItems = useCallback(
    (query: string): MentionItem[] => {
      const needle = fold(query);

      const people = usersRef.current
        .filter((user) => user.id !== meId || needle.length > 0)
        .filter(
          (user) =>
            fold(user.displayName).includes(needle) || fold(user.nickname ?? '').includes(needle),
        )
        .slice(0, 8)
        .map<MentionItem>((user) => ({
          id: user.id,
          label: user.displayName,
          kind: 'user',
          hint: user.title ?? '',
        }));

      const groupItems = groupsRef.current
        .filter((group) => fold(group.handle).includes(needle) || fold(group.name).includes(needle))
        .slice(0, 5)
        .map<MentionItem>((group) => ({
          id: group.id,
          label: group.handle,
          kind: 'group',
          hint: `${group.memberIds.length} fő`,
        }));

      const special: MentionItem[] = allowChannelMentions
        ? (
            [
              { id: 'channel', label: 'channel', kind: 'channel', hint: 'a csatorna minden tagja' },
              { id: 'here', label: 'here', kind: 'here', hint: 'az éppen elérhetők' },
            ] as MentionItem[]
          ).filter((item) => fold(item.label).includes(needle))
        : [];

      return [...special, ...groupItems, ...people];
    },
    [allowChannelMentions, meId],
  );

  const allowEmptyRef = useRef(allowEmpty);
  allowEmptyRef.current = allowEmpty;

  const submit = useCallback((editor: Editor) => {
    const body = editor.getJSON() as RichTextDoc;
    const text = docToPlainText(body);
    if (!text && !allowEmptyRef.current) return;

    submitRef.current({ text, body });

    /*
      Kuldes utan a fokusz VISSZAKERUL a szerkesztobe.

      Enterrel nyomva ez magatol is igy lenne, de a kuldes gombra kattintva nem:
      a kattintas elviszi a fokuszt, es a kovetkezo mondatot a felhasznalonak
      ujra bele kellene kattintania. Egy chatben a kuldes utani allapot
      alapertelmezesben az, hogy irunk tovabb.
    */
    editor.chain().clearContent(true).focus().run();
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Chatben nincs cimsor es valaszto vonal; a link sajat beallitast kap.
        heading: false,
        horizontalRule: false,
        link: {
          openOnClick: false,
          autolink: true,
          protocols: ['http', 'https', 'mailto'],
          isAllowedUri: (url) => SAFE_URL_RE.test(url),
          HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
        },
      }),
      /*
        A felirat REFBOL jon, nem a letrehozaskori ertekbol.

        A szerkeszto egyszer epul fel, a felirat viszont kesobb is valtozhat -
        a telefonos csatorna neve peldaul csak a vonal adatainak megerkezese
        utan all ossze. Enelkul ott ragadt a nyers "telefon-3612204899".
      */
      Placeholder.configure({ placeholder: () => placeholderRef.current }),

      /*
        Gepeles kozbeni atalakitas: `:)` -> 🙂, `:salute:` -> 🫡.

        Az egyedi emojikat kihagyja: azok `:nev:` alakban maradnak, abbol
        keszit kepet a megjelenites.
      */
      createEmojiInputRules({
        hasCustomEmoji: (name) => customEmojiRef.current.has(name),
      }),
      Mention.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            kind: {
              default: 'user',
              parseHTML: (element) => element.getAttribute('data-kind') ?? 'user',
              renderHTML: (attributes) => ({ 'data-kind': String(attributes.kind ?? 'user') }),
            },
          };
        },
      }).configure({
        HTMLAttributes: { class: 'avslack-mention' },
        suggestion: {
          char: '@',
          items: ({ query }) => buildItems(query),
          command: ({ editor: instance, range, props }) => {
            const item = props as unknown as MentionItem;
            instance
              .chain()
              .focus()
              .insertContentAt(range, [
                {
                  type: 'mention',
                  attrs: { id: item.id, label: item.label, kind: item.kind },
                },
                { type: 'text', text: ' ' },
              ])
              .run();
          },
          render: () => ({
            onStart: (props) => {
              setCursor(0);
              setSuggestion({
                items: props.items as MentionItem[],
                command: props.command as unknown as (item: MentionItem) => void,
                rect: props.clientRect?.() ?? null,
              });
            },
            onUpdate: (props) => {
              setSuggestion({
                items: props.items as MentionItem[],
                command: props.command as unknown as (item: MentionItem) => void,
                rect: props.clientRect?.() ?? null,
              });
            },
            onKeyDown: (props) => handleSuggestionKey(props.event),
            onExit: () => setSuggestion(null),
          }),
        },
      }),

      /**
       * EMOJI a `:` utan.
       *
       * Nem `Mention`-re epul, mert nem csomopontot szurunk be, hanem sima
       * szoveget: a beepitett jelet magat, vagy az egyedi emoji `:nev:` alakjat
       * (abbol a megjelenites keszit kepet). Igy a kesz uzenetben nincs
       * kulonbseg a begepelt es a listabol valasztott emoji kozott.
       *
       * Az `allowedPrefixes` a lenyeg: a kettospont csak sor elejen vagy szokoz
       * utan indit javaslatot. Enelkul a "12:30" es a "https://" is felhozna a
       * listat - epp gepeles kozben, a legrosszabb pillanatban.
       */
      Extension.create({
        name: 'emojiSuggestion',
        addProseMirrorPlugins() {
          return [
            Suggestion<MentionItem>({
              editor: this.editor,
              char: ':',
              allowSpaces: false,
              allowedPrefixes: [' '],
              startOfLine: false,
              items: ({ query }) => buildEmojiItems(query),
              command: ({ editor: instance, range, props }) => {
                instance
                  .chain()
                  .focus()
                  .insertContentAt(range, `${props.insert ?? props.label} `)
                  .run();
              },
              render: () => ({
                onStart: (props) => {
                  setCursor(0);
                  setSuggestion({
                    items: props.items,
                    command: props.command as unknown as (item: MentionItem) => void,
                    rect: props.clientRect?.() ?? null,
                  });
                },
                onUpdate: (props) => {
                  setSuggestion({
                    items: props.items,
                    command: props.command as unknown as (item: MentionItem) => void,
                    rect: props.clientRect?.() ?? null,
                  });
                },
                onKeyDown: (props) => handleSuggestionKey(props.event),
                onExit: () => setSuggestion(null),
              }),
            }),
          ];
        },
      }),
    ],
    content: initial ?? '',
    autofocus: autoFocus,
    editorProps: {
      attributes: {
        class: [
          'avslack-prose w-full outline-none',
          compact ? 'max-h-40 text-sm' : 'max-h-72 text-base',
          'overflow-y-auto leading-6',
        ].join(' '),
      },
      /**
       * Beillesztett kod automatikusan kodblokkba kerul.
       *
       * Enelkul a behuzas elveszik, a hosszu sorok tordelodnek, es a masolas
       * visszafele mar nem ugyanazt adja - ami egy hibauzenetnel vagy egy
       * lekerdezesnel pont a lenyeg. A felismeres ovatos (lasd shared/code.ts),
       * es a Ctrl+Z egyetlen lepesben visszavonja.
       *
       * Kodblokkon BELUL nem nyulunk a beillesztéshez: ott a felhasznalo mar
       * megmondta, mit akar.
       */
      handlePaste: (view, event) => {
        const text = event.clipboardData?.getData('text/plain');
        if (!text) return false;
        if (view.state.selection.$from.parent.type.name === 'codeBlock') return false;

        const guess = detectCode(text);
        if (!guess.isCode) return false;

        /**
         * Kozvetlenul a ProseMirror tranzakciojaval dolgozunk.
         *
         * A magasabb szintu `insertContent` ures dokumentumnal egy ures
         * kodblokkot hagyott a beszurt mellett. A kijeloles cserejenel ez nem
         * fordulhat elo: pontosan azt valtjuk le, ami ki van jelolve.
         */
        const body = text.replace(/\s+$/, '');
        const codeBlock = view.state.schema.nodes.codeBlock;
        if (!codeBlock) return false;

        event.preventDefault();
        view.dispatch(
          view.state.tr
            .replaceSelectionWith(
              codeBlock.create({ language: guess.language }, view.state.schema.text(body)),
            )
            .scrollIntoView(),
        );
        return true;
      },
      handleKeyDown: (_view, event) => {
        // Amig az emlites-lista nyitva van, az Enter azt valasztja, nem kuld.
        if (suggestionRef.current) return false;

        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          if (editorRef.current) submit(editorRef.current);
          return true;
        }
        if (event.key === 'Escape' && onCancel) {
          event.preventDefault();
          onCancel();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: instance }) => {
      setIsEmpty(instance.isEmpty);
      if (!instance.isEmpty) onTyping?.();

      const body = instance.getJSON() as RichTextDoc;
      changeRef.current?.({ text: docToPlainText(body), body });
    },
  });

  // A felirat valtozasa utan a szerkeszto ujrarajzolja a diszitest.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.view.dispatch(editor.state.tr.setMeta('placeholder', placeholder));
  }, [editor, placeholder]);

  const editorRef = useRef<Editor | null>(null);
  editorRef.current = editor;

  useEffect(() => {
    if (editor) setIsEmpty(editor.isEmpty);
  }, [editor]);

  /**
   * A kesobb betolto piszkozat behelyezese - EGYSZER.
   *
   * A piszkozat a szervertol jon, tehat gyakran kesobb erkezik, mint ahogy a
   * szerkeszto letrejon; a `content` viszont csak a letrehozaskor szamit.
   *
   * Korabban ezt egy `key`-valtas oldotta meg a hivo oldalon - az viszont a
   * szerkesztot IS ujraepitette, iras kozben is, a fokusszal egyutt.
   *
   * Ket dolgot orzunk: csak akkor irunk be, ha a szerkeszto MEG URES (amit a
   * felhasznalo gepelt, azt sosem irjuk felul), es csak egyszer.
   */
  const draftLoaded = useRef(false);

  useEffect(() => {
    if (!editor || draftLoaded.current) return;
    if (!initial) return;

    if (editor.isEmpty) {
      editor.commands.setContent(initial, { emitUpdate: false });
      setIsEmpty(editor.isEmpty);
    }

    draftLoaded.current = true;
  }, [editor, initial]);

  useEffect(() => {
    if (!editor || !onReady) return;
    onReady({
      clear: () => {
        editor.commands.clearContent(true);
        setIsEmpty(true);
      },
      getValue: () => {
        const body = editor.getJSON() as RichTextDoc;
        const text = docToPlainText(body);
        return text ? { text, body } : null;
      },
      insert: (text: string) => {
        // A fokusz visszaadasa fontos: a valaszto elvette, es a felhasznalo
        // a beszuras utan tovabb akar gepelni.
        editor.chain().focus().insertContent(text).run();
      },
      focus: () => editor.commands.focus(),
    });
  }, [editor, onReady]);

  const activeIndex = useMemo(
    () => (suggestion ? Math.min(cursor, suggestion.items.length - 1) : 0),
    [cursor, suggestion],
  );

  return (
    <div className="relative">
      {/*
        Formazo eszkoztar - MINDIG latszik.

        Korabban csak fokuszban jelent meg: "nyugalmi allapotban csendes marad
        a felulet". A gyakorlatban ez azt jelentette, hogy a gombok pont akkor
        nem voltak ott, amikor az ember rajuk nezett volna - a szerkesztobe
        kellett elobb kattintani, hogy kiderujon, egyaltalan lehet-e formazni.
        Egy eszkoztar, ami elbujik, nem eszkoztar.
      */}
      {editor && <FormatBar editor={editor} compact={compact} end={toolbarEnd} />}

      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <EditorContent editor={editor} />
        </div>

        <button
          type="button"
          onClick={() => editor && submit(editor)}
          disabled={isEmpty && !allowEmpty}
          title={`${submitLabel} (Enter)`}
          aria-label={submitLabel}
          className={[
            compact ? 'size-7 text-xs' : 'size-9 text-sm',
            'grid shrink-0 place-items-center rounded-control transition-colors duration-(--duration-quick)',
            isEmpty && !allowEmpty
              ? 'bg-surface-sunken text-ink-faint'
              : 'bg-accent text-accent-ink hover:bg-accent-hover',
          ].join(' ')}
        >
          <Icon name="nyilFel" />
        </button>
      </div>

      {suggestion && suggestion.items.length > 0 && (
        <MentionList
          items={suggestion.items}
          activeIndex={activeIndex}
          rect={suggestion.rect}
          onPick={(item) => suggestion.command(item)}
          onHover={setCursor}
        />
      )}
    </div>
  );
}

function MentionList({
  items,
  activeIndex,
  rect,
  onPick,
  onHover,
}: {
  items: MentionItem[];
  activeIndex: number;
  rect: DOMRect | null;
  onPick: (item: MentionItem) => void;
  onHover: (index: number) => void;
}) {
  // A lista a kurzor fole kerul, fix pozicioval - igy nem vagja el a szerkeszto
  // gorgetheto teruletenek a hatara.
  const style = rect
    ? { left: Math.max(8, rect.left), bottom: window.innerHeight - rect.top + 8 }
    : { left: 8, bottom: 80 };

  return (
    <ul
      style={style}
      className="animate-fade-rise fixed z-50 max-h-64 w-72 overflow-y-auto rounded-card border border-border-soft bg-surface-raised py-1 shadow-(--shadow-modal)"
    >
      {items.map((item, index) => (
        <li key={`${item.kind}-${item.id}`}>
          <button
            type="button"
            onMouseEnter={() => onHover(index)}
            onMouseDown={(e) => {
              e.preventDefault(); // ne vesszen el a szerkeszto fokusza
              onPick(item);
            }}
            className={[
              'flex w-full items-center gap-2.5 px-3 py-1.5 text-left',
              index === activeIndex ? 'bg-accent-soft' : '',
            ].join(' ')}
          >
            {item.kind === 'emoji' ? (
              /*
                Az ELONEZET maga a valasztas: a nev alapjan nem lehet eldonteni,
                melyik jel jon. Egyedi emojinal kep, beepitettnel a jel maga.
              */
              <span className="grid size-6 shrink-0 place-items-center text-base">
                {item.previewUrl ? (
                  <img src={item.previewUrl} alt="" className="size-5 object-contain" />
                ) : (
                  item.previewChar
                )}
              </span>
            ) : item.kind === 'user' ? (
              <Avatar userId={item.id} name={item.label} size="sm" />
            ) : (
              <span
                className={[
                  'grid size-6 shrink-0 place-items-center rounded text-2xs font-bold',
                  item.kind === 'group' ? 'bg-accent-soft text-accent' : 'bg-alert-soft text-alert',
                ].join(' ')}
              >
                @
              </span>
            )}
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{item.label}</span>
            {item.hint && <span className="shrink-0 text-2xs text-ink-faint">{item.hint}</span>}
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * Formazo eszkoztar.
 *
 * A gombok MELLETT ott a billentyuparancs is: aki egyszer megnezi, legkozelebb
 * mar nem nyul az egerhez. A gomb az aktualis allapotot is mutatja - ha a
 * kurzor felkoveren all, a gomb be van nyomva.
 */
function FormatBar({
  editor,
  compact,
  end,
}: {
  editor: Editor;
  compact: boolean;
  end?: React.ReactNode;
}) {
  const items = [
    {
      name: 'felkover',
      label: 'Felkövér',
      keys: 'Ctrl+B',
      mark: 'bold',
      run: () => editor.chain().focus().toggleBold().run(),
    },
    {
      name: 'dolt',
      label: 'Dőlt',
      keys: 'Ctrl+I',
      mark: 'italic',
      run: () => editor.chain().focus().toggleItalic().run(),
    },
    {
      name: 'alahuzott',
      label: 'Aláhúzott',
      keys: 'Ctrl+U',
      mark: 'underline',
      run: () => editor.chain().focus().toggleUnderline().run(),
    },
    {
      name: 'athuzott',
      label: 'Áthúzott',
      keys: 'Ctrl+Shift+X',
      mark: 'strike',
      run: () => editor.chain().focus().toggleStrike().run(),
    },
    {
      name: 'kod',
      label: 'Kód',
      keys: 'Ctrl+E',
      mark: 'code',
      run: () => editor.chain().focus().toggleCode().run(),
    },
    {
      name: 'idezet',
      label: 'Idézet',
      keys: 'Ctrl+Shift+B',
      mark: 'blockquote',
      run: () => editor.chain().focus().toggleBlockquote().run(),
    },
    {
      name: 'listaPontos',
      label: 'Felsorolás',
      keys: 'Ctrl+Shift+8',
      mark: 'bulletList',
      run: () => editor.chain().focus().toggleBulletList().run(),
    },
    {
      name: 'listaSzamozott',
      label: 'Számozott lista',
      keys: 'Ctrl+Shift+7',
      mark: 'orderedList',
      run: () => editor.chain().focus().toggleOrderedList().run(),
    },
  ] as const;

  return (
    <div
      className={`mb-1.5 flex flex-wrap items-center gap-0.5 border-b border-border-soft pb-1.5 ${
        compact ? 'text-xs' : 'text-sm'
      }`}
    >
      {items.map((item) => {
        const active = editor.isActive(item.mark);
        return (
          <button
            key={item.name}
            type="button"
            title={`${item.label} (${item.keys})`}
            aria-label={item.label}
            aria-pressed={active}
            // A `mousedown` megallitasa nelkul a kattintas elvenne a fokuszt a
            // szerkesztotol, es a formazas a semmire vonatkozna.
            onMouseDown={(event) => event.preventDefault()}
            onClick={item.run}
            className={`grid size-6 place-items-center rounded-control transition-colors duration-(--duration-quick) sm:size-7 ${
              active
                ? 'bg-accent-soft text-accent'
                : 'text-ink-faint hover:bg-surface-hover hover:text-ink'
            }`}
          >
            <Icon name={item.name} size="sm" />
          </button>
        );
      })}
      {end && <div className="ml-auto flex items-center">{end}</div>}
    </div>
  );
}
